import React from "react";
import { Row, Col, Checkbox, Button, Divider, Space, Typography, Tooltip as AntTooltip, Tag } from "antd";
import { PlusSquareOutlined, MinusSquareOutlined } from "@ant-design/icons";
import {
  ALL_COLUMNS,
  PINNED_KEYS,
  COL_POPOVER_WIDTH,
  CHILD_LABEL_WIDTH,
} from "../../../../util/helper";

const { Text } = Typography;

const SubBox = ({
  title,
  keyName,
  kids,
  expanded,
  onToggle,
  isChecked,
  onParentChange,
  visibleChildren,
  onChildChange,
  disabled = false,
}) => {
  if (disabled) {
    return (
      <div
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 6,
          padding: "8px 12px",
          minWidth: 280,
          background: "#fafafa",
          opacity: 0.55,
          userSelect: "none",
        }}
      >
        <Space>
          <Checkbox disabled checked={false}>
            <Text strong type="secondary">{title}</Text>
          </Checkbox>
          <Tag style={{ marginLeft: 4, fontSize: 11 }}>No data</Tag>
        </Space>
      </div>
    );
  }

  return (
    <div style={{ border: "1px solid #f0f0f0", borderRadius: 6, padding: 8, minWidth: 280 }}>
      <Space>
        <Button
          type="text"
          size="small"
          icon={expanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
          onClick={onToggle}
        />
        <Checkbox checked={isChecked} onChange={onParentChange}>
          <Text strong>{title}</Text>
        </Checkbox>
      </Space>
      {expanded && (
        <div
          style={{
            marginTop: 8,
            paddingLeft: "12px",
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${CHILD_LABEL_WIDTH}px, 1fr))`,
            gap: "6px 10px",
          }}
        >
          <Checkbox.Group value={visibleChildren} onChange={onChildChange} style={{ display: "contents" }}>
            {kids.map((sub) => {
              const label =
                keyName === "adme"
                  ? `${sub.parent}(${sub.child})`
                  : keyName === "molprops"
                    ? sub.title
                    : `${sub.file}, pH=${sub.ph_threshold}`;
              return (
                <Checkbox key={sub.key} value={sub.key} style={{ margin: 0 }}>
                  <AntTooltip title={label}>
                    <span
                      style={{
                        display: "inline-block",
                        maxWidth: CHILD_LABEL_WIDTH,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {label}
                    </span>
                  </AntTooltip>
                </Checkbox>
              );
            })}
          </Checkbox.Group>
        </div>
      )}
    </div>
  );
};

export const ColumnSettingsPopover = ({
  visibleColumns,
  setVisibleColumns,
  expandedGroups,
  setExpandedGroups,
  handleParentChange,
  handleChildChange,
  handleSelectAllColumns,
  allColumnsSelected,
  someColumnsSelected,
  dynamicColumnGroups,
}) => {
  const { dynamicGlideColumns, dynamicPhaseColumns, dynamicAdmeColumns, dynamicMolProps } = dynamicColumnGroups;

  const glideDisabled = dynamicGlideColumns.length === 0;
  const phaseDisabled = dynamicPhaseColumns.length === 0;
  const admeDisabled = dynamicAdmeColumns.length === 0;
  const molPropsDisabled = dynamicMolProps.length === 0;

  return (
    <div style={{ width: COL_POPOVER_WIDTH, maxHeight: 560, padding: 12, overflowY: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
        <Checkbox
          indeterminate={someColumnsSelected}
          checked={allColumnsSelected}
          onChange={handleSelectAllColumns}
        >
          Select All
        </Checkbox>
        <Divider type="vertical" />
        <Text strong>Essentials:</Text>
        {PINNED_KEYS.map((key) => {
          const meta = ALL_COLUMNS.find((column) => column.key === key);
          return (
            <Checkbox
              key={key}
              checked={visibleColumns.includes(key)}
              onChange={(event) => {
                if (event.target.checked) setVisibleColumns((prev) => [...prev, key]);
                else setVisibleColumns((prev) => prev.filter((columnKey) => columnKey !== key));
              }}
              disabled={meta?.disabled}
            >
              {meta?.title}
            </Checkbox>
          );
        })}
      </div>

      <Divider style={{ margin: "8px 0" }} />

      <Row gutter={[16, 16]}>
        <Col flex="1">
          <div style={{ border: "1px solid #f0f0f0", borderRadius: 6, padding: 8 }}>
            <Space>
              <Button
                type="text"
                size="small"
                icon={expandedGroups.schrodinger ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
                onClick={() => setExpandedGroups((prev) => ({ ...prev, schrodinger: !prev.schrodinger }))}
              />
              <Text strong>Schrodinger Jobs</Text>
            </Space>

            {expandedGroups.schrodinger && (
              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                <Col>
                  <SubBox
                    title="Glide Docking"
                    keyName="glide"
                    kids={dynamicGlideColumns}
                    expanded={expandedGroups.glide}
                    onToggle={() => setExpandedGroups((prev) => ({ ...prev, glide: !prev.glide }))}
                    isChecked={visibleColumns.includes("glide")}
                    onParentChange={(event) => handleParentChange(event, "glide", dynamicGlideColumns)}
                    visibleChildren={visibleColumns.filter((key) => key.startsWith("glide_"))}
                    onChildChange={(keys) => handleChildChange(keys, "glide")}
                    disabled={glideDisabled}
                  />
                </Col>

                <Col>
                  <SubBox
                    title="Pharmacophore"
                    keyName="phase"
                    kids={dynamicPhaseColumns}
                    expanded={expandedGroups.phase}
                    onToggle={() => setExpandedGroups((prev) => ({ ...prev, phase: !prev.phase }))}
                    isChecked={visibleColumns.includes("phase")}
                    onParentChange={(event) => handleParentChange(event, "phase", dynamicPhaseColumns)}
                    visibleChildren={visibleColumns.filter((key) => key.startsWith("phase_"))}
                    onChildChange={(keys) => handleChildChange(keys, "phase")}
                    disabled={phaseDisabled}
                  />
                </Col>

                <Col>
                  <SubBox
                    title="ADME Properties"
                    keyName="adme"
                    kids={dynamicAdmeColumns}
                    expanded={expandedGroups.adme}
                    onToggle={() => setExpandedGroups((prev) => ({ ...prev, adme: !prev.adme }))}
                    isChecked={visibleColumns.includes("adme")}
                    onParentChange={(event) => handleParentChange(event, "adme", dynamicAdmeColumns)}
                    visibleChildren={visibleColumns.filter((key) => key.startsWith("adme_"))}
                    onChildChange={(keys) => handleChildChange(keys, "adme")}
                    disabled={admeDisabled}
                  />
                </Col>
              </Row>
            )}
          </div>
        </Col>

        <Col>
          <SubBox
            title="Molecular Properties"
            keyName="molprops"
            kids={dynamicMolProps}
            expanded={expandedGroups.molprops}
            onToggle={() => setExpandedGroups((prev) => ({ ...prev, molprops: !prev.molprops }))}
            isChecked={visibleColumns.includes("molprops")}
            onParentChange={(event) => handleParentChange(event, "molprops", dynamicMolProps)}
            visibleChildren={visibleColumns.filter((key) => key.startsWith("molprops_"))}
            onChildChange={(keys) => handleChildChange(keys, "molprops")}
            disabled={molPropsDisabled}
          />
        </Col>
      </Row>
    </div>
  );
};
